// src/components/Pagination.tsx
//
// ★★ ONE page-number control, shared by every table the gateway pages with an
// `?offset=` — not a copy per page. `SortableTh` establishes the pattern and the
// reason: three slightly different copies of a control is three places an
// accessibility property has to be got right, and it will not be.
//
// ══ WHAT IT HAS TO GET RIGHT, AND WHY EACH IS HERE ══
//
//   * **A real `<nav>` with an accessible name.** A row of buttons is not
//     navigation to a screen reader unless it says it is, and the name has to
//     distinguish THIS table's pager from another one on the same page — hence
//     `label`, which every caller supplies.
//   * **The position is TEXT, not only an arrow.** "Page 3 of 9" is read out;
//     an enabled/disabled arrow is not. It also answers the question the officer
//     actually has, which is how much is left.
//   * **`aria-live="polite"` on that text**, so moving pages is announced. A
//     control that changes the table underneath it silently is the single most
//     common paging accessibility defect.
//   * **`disabled`, never hidden.** A control that disappears at the edges makes
//     the row jump and moves the button the officer is about to click. Disabled
//     keeps the layout and states the boundary.
//   * **Every button says what it does in words**, with the glyph
//     `aria-hidden`. `‹` is not a word.
//   * **`pt-no-print`**, the same as every other control in this app: a printed
//     page cannot be paged, and P2C1 §12.52 requires these surfaces printable.
//
// ⚠ **WHAT IT DELIBERATELY DOES NOT DO.** No numbered page buttons (1 2 3 … 9).
// They are a lot of DOM, they need an ellipsis algorithm, and on these
// populations — administrator-edited catalogues, capped at 100 rows a page —
// first/prev/next/last plus a stated position covers the real journeys. If a
// surface ever genuinely needs "jump to page 40", it needs a filter more.
//
// ⚠ **It renders NOTHING when there is one page or fewer.** A pager over a
// single page is furniture that implies more content exists.
import { SgdsButton } from '@govtechsg/sgds-web-component/react'
import { IconButton } from './IconButton'
import type { UseOffsetPagerResult } from '../lib/useOffsetPager'

export interface PaginationProps {
  readonly pager: UseOffsetPagerResult
  /**
   * What is being paged, lower case and plural — "audit events", "contacts".
   *
   * Used in the `<nav>`'s accessible name and in the position text, so a screen
   * reader user hears which table they are moving through rather than a bare
   * "page 3 of 9" that could belong to anything on the screen.
   */
  readonly label: string
  /**
   * The population size, for the "of N" half of the position text — or
   * `undefined` where the surface cannot say.
   *
   * ⚠ `GET /api/audit` reports no total, deliberately: a filtered `count(*)`
   * over the largest append-only table in the product, on every page view, is
   * what P2C14 §6.1.1's 1 s p95 does not survive. The text below degrades to
   * "page 3, showing audit events 15–21" rather than inventing a denominator.
   */
  readonly total: number | undefined
  /** Rows on the CURRENT page — so the text can say 7 of 412 rather than implying a full page. */
  readonly shown: number
}

export function Pagination({ pager, label, total, shown }: PaginationProps) {
  // One page or none: nothing to navigate. See the header.
  //
  // ⚠ With no total there is no page count to test, so the test becomes "is
  // there anywhere to go?" — which is the same question, asked the only way
  // this surface can answer it.
  const single = pager.pageCount === undefined ? !pager.canGoNext && !pager.canGoPrevious : pager.pageCount <= 1
  if (single) return null

  const first = pager.offset + 1
  const last = pager.offset + shown

  return (
    <nav
      className="pt-no-print flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-t border-[var(--sgds-border-color-muted)]"
      aria-label={`${label} pagination`}
    >
      {/*
        ⚠ `aria-live="polite"` and NOT `assertive`: the officer pressed the
        button, so they are expecting the change and do not need it to interrupt
        whatever else is speaking. `aria-atomic` so the whole sentence is
        re-read rather than only the digit that changed, which on its own is
        meaningless ("nine").
      */}
      <p className="text-sm text-[var(--sgds-color-subtle)] m-0" aria-live="polite" aria-atomic="true">
        {pager.pageCount === undefined
          ? `Page ${pager.pageNumber}: showing ${label} ${first} to ${last}.`
          : `Page ${pager.pageNumber} of ${pager.pageCount}: showing ${label} ${first} to ${last} of ${total}.`}
      </p>

      <div className="flex items-center gap-2">
        <IconButton glyph="first" label="First page" disabled={!pager.canGoPrevious} onClick={pager.goFirst} />
        <SgdsButton
          variant="outline"
          size="sm"
          type="button"
          disabled={!pager.canGoPrevious}
          onClick={pager.goPrevious}
        >
          <span aria-hidden="true">‹</span> Previous page
        </SgdsButton>
        <SgdsButton variant="outline" size="sm" type="button" disabled={!pager.canGoNext} onClick={pager.goNext}>
          Next page <span aria-hidden="true">›</span>
        </SgdsButton>
        {/*
          ⚠ OMITTED, not disabled, where the surface reports no total: "last" is
          unreachable without knowing where the end is, and a permanently
          disabled button is a promise the product cannot keep. Everywhere else
          it is disabled rather than hidden, for the reason in the header — this
          is the one case where the control genuinely does not exist.
        */}
        {pager.canGoLast ? (
          <IconButton glyph="last" label="Last page" disabled={!pager.canGoNext} onClick={pager.goLast} />
        ) : null}
      </div>
    </nav>
  )
}
